/**
 * Mock data — base de dados fictícia para o app rodar sem backend.
 * Estado in-memory; reseta a cada reload. Mantém usuários cadastrados via
 * register durante a sessão. Dados brasileiros realistas para o evento
 * Know How Summit 2026 (Campo Grande/MS, Maio/2026).
 */

import type { AuthUser } from '@/shared/stores/authStore';
import type { Tag } from '@/entities/tag';
import type { Match } from '@/entities/match';
import type { Conversation, Message } from '@/entities/message';
import type { CrmEntry } from '@/entities/crm-entry';
import type { Event } from '@/entities/event';

export const MOCK_ACCESS_TOKEN = 'mock.eyJ1c2VySWQiOiJ1LWRlbW8iLCJleHAiOjk5OTk5OTk5OTl9.signature';

const minutesAgo = (m: number) => new Date(Date.now() - m * 60_000).toISOString();
const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();
const daysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();

export const mockCurrentUser: AuthUser = {
  id: 'u-demo',
  email: 'demo@knowhow.com',
  nome: 'Henrique Costa',
  fotoUrl: null,
  onboardingCompleto: true,
  plano: 'PREMIUM',
};

// ─────────────────────────────────── TAGS (30 em 6 categorias) ───────────────
export const mockTags: Tag[] = [
  // Vendas
  { id: 't-vendas-1', nome: 'Vendas B2B', categoria: 'BUSCA' },
  { id: 't-vendas-2', nome: 'Vendas Consultivas', categoria: 'BUSCA' },
  { id: 't-vendas-3', nome: 'Outbound', categoria: 'BUSCA' },
  { id: 't-vendas-4', nome: 'Inside Sales', categoria: 'OFERTA' },
  { id: 't-vendas-5', nome: 'Account Management', categoria: 'OFERTA' },
  // Marketing
  { id: 't-mkt-1', nome: 'Marketing Digital', categoria: 'BUSCA' },
  { id: 't-mkt-2', nome: 'Branding', categoria: 'BUSCA' },
  { id: 't-mkt-3', nome: 'Performance Ads', categoria: 'OFERTA' },
  { id: 't-mkt-4', nome: 'SEO & Conteúdo', categoria: 'OFERTA' },
  { id: 't-mkt-5', nome: 'Growth Hacking', categoria: 'OFERTA' },
  // Gestão
  { id: 't-gest-1', nome: 'Gestão Estratégica', categoria: 'BUSCA' },
  { id: 't-gest-2', nome: 'OKRs', categoria: 'BUSCA' },
  { id: 't-gest-3', nome: 'Mentoria de CEO', categoria: 'OFERTA' },
  { id: 't-gest-4', nome: 'Cultura Organizacional', categoria: 'OFERTA' },
  { id: 't-gest-5', nome: 'Operações & Processos', categoria: 'OFERTA' },
  // Tecnologia
  { id: 't-tech-1', nome: 'Tecnologia / SaaS', categoria: 'GERAL' },
  { id: 't-tech-2', nome: 'Transformação Digital', categoria: 'BUSCA' },
  { id: 't-tech-3', nome: 'Produto & UX', categoria: 'OFERTA' },
  { id: 't-tech-4', nome: 'IA & Automação', categoria: 'OFERTA' },
  { id: 't-tech-5', nome: 'Engenharia de Software', categoria: 'OFERTA' },
  // Financeiro
  { id: 't-fin-1', nome: 'Captação de Investidores', categoria: 'BUSCA' },
  { id: 't-fin-2', nome: 'M&A e Investimentos', categoria: 'OFERTA' },
  { id: 't-fin-3', nome: 'Reestruturação Financeira', categoria: 'OFERTA' },
  { id: 't-fin-4', nome: 'Planejamento Tributário', categoria: 'OFERTA' },
  { id: 't-fin-5', nome: 'FP&A', categoria: 'OFERTA' },
  // Jurídico/Outros
  { id: 't-jur-1', nome: 'Consultoria Estratégica', categoria: 'OFERTA' },
  { id: 't-jur-2', nome: 'Parceria Comercial', categoria: 'OFERTA' },
  { id: 't-jur-3', nome: 'Recrutamento Executivo', categoria: 'BUSCA' },
  { id: 't-jur-4', nome: 'Expansão Nacional', categoria: 'BUSCA' },
  { id: 't-jur-5', nome: 'Compliance & Jurídico', categoria: 'OFERTA' },
];

// ─────────────────────────────────── EVENTO ───────────────────────────────────
export const mockEvent: Event = {
  id: 'e-1',
  nome: 'Know How Summit 2026',
  dataInicio: '2026-05-22T09:00:00.000Z',
  dataFim: '2026-05-23T18:00:00.000Z',
  local: 'Centro de Convenções Bosque dos Ipês',
  cidade: 'Campo Grande',
  estado: 'MS',
  ativo: true,
};

// ─────────────────────────────────── MATCHES (12) ─────────────────────────────
export const mockMatches: Match[] = [
  {
    id: 'm-1',
    status: 'SUGERIDO',
    score: 147,
    altoPotencial: true,
    motivos: [
      'Atua em Tecnologia/SaaS, mesmo segmento que você',
      'Busca M&A e Investimentos, você oferece exatamente isso',
      'Mesma região: MS',
    ],
    participante: {
      id: 'u-2',
      nome: 'Ana Beatriz Soares',
      fotoUrl: null,
      cargo: 'CEO',
      empresa: 'NovaTech Pagamentos',
      tipoPerfil: 'EMPRESARIO',
      bio: 'Fintech B2B com 18 meses, faturando R$ 4M ARR. Buscando rodada Series A.',
      tagsBusca: [
        { id: 't-fin-2', nome: 'M&A e Investimentos' },
        { id: 't-jur-1', nome: 'Consultoria Estratégica' },
      ],
      tagsOferta: [
        { id: 't-jur-2', nome: 'Parceria Comercial' },
        { id: 't-vendas-1', nome: 'Vendas B2B' },
      ],
    },
    createdAt: minutesAgo(12),
  },
  {
    id: 'm-2',
    status: 'SUGERIDO',
    score: 142,
    altoPotencial: true,
    motivos: [
      'Mentor de 80+ founders, perfeito para sua fase',
      'Oferece Operações & Processos, seu gargalo declarado',
    ],
    participante: {
      id: 'u-3',
      nome: 'Rafael Almeida',
      fotoUrl: null,
      cargo: 'Sócio-Diretor',
      empresa: 'Almeida & Partners',
      tipoPerfil: 'CONSULTOR',
      bio: 'Mentor de 80+ founders desde 2019. Foco em escalar de 1M a 10M ARR.',
      tagsBusca: [{ id: 't-jur-4', nome: 'Expansão Nacional' }],
      tagsOferta: [
        { id: 't-gest-3', nome: 'Mentoria de CEO' },
        { id: 't-gest-5', nome: 'Operações & Processos' },
      ],
    },
    createdAt: hoursAgo(1),
  },
  {
    id: 'm-3',
    status: 'CONVERSANDO',
    score: 138,
    altoPotencial: true,
    motivos: ['Match já em conversa ativa', 'Investidor alinhado com sua fase de crescimento'],
    participante: {
      id: 'u-5',
      nome: 'Carlos Mendes',
      fotoUrl: null,
      cargo: 'Partner',
      empresa: 'Latitude Capital',
      tipoPerfil: 'INVESTIDOR',
      bio: 'Fundo early-stage focado em B2B SaaS LatAm. Ticket: R$ 2M-8M.',
      tagsBusca: [{ id: 't-tech-1', nome: 'Tecnologia / SaaS' }],
      tagsOferta: [{ id: 't-fin-2', nome: 'M&A e Investimentos' }],
    },
    createdAt: hoursAgo(26),
  },
  {
    id: 'm-4',
    status: 'SUGERIDO',
    score: 124,
    altoPotencial: false,
    motivos: ['Mesma fase de Crescimento', 'Setor adjacente: Saúde Digital'],
    participante: {
      id: 'u-4',
      nome: 'Marina Yamashita',
      fotoUrl: null,
      cargo: 'Diretora Comercial',
      empresa: 'ClinicaMais',
      tipoPerfil: 'EXECUTIVO',
      bio: 'Rede de clínicas com 32 unidades. Buscando parceria de tecnologia.',
      tagsBusca: [{ id: 't-tech-1', nome: 'Tecnologia / SaaS' }],
      tagsOferta: [{ id: 't-jur-2', nome: 'Parceria Comercial' }],
    },
    createdAt: hoursAgo(8),
  },
  {
    id: 'm-5',
    status: 'SUGERIDO',
    score: 118,
    altoPotencial: false,
    motivos: ['Mesma região: MS', 'Complementaridade em Performance Ads'],
    participante: {
      id: 'u-6',
      nome: 'Pedro Henrique Lima',
      fotoUrl: null,
      cargo: 'Head of Growth',
      empresa: 'Agência Plural',
      tipoPerfil: 'EXECUTIVO',
      bio: 'Lidera growth para 15+ clientes B2B. Especialista em performance pago.',
      tagsBusca: [{ id: 't-vendas-1', nome: 'Vendas B2B' }],
      tagsOferta: [
        { id: 't-mkt-3', nome: 'Performance Ads' },
        { id: 't-mkt-5', nome: 'Growth Hacking' },
      ],
    },
    createdAt: hoursAgo(14),
  },
  {
    id: 'm-6',
    status: 'SUGERIDO',
    score: 145,
    altoPotencial: true,
    motivos: ['Especialista em IA & Automação', 'Atua em Tecnologia/SaaS, mesmo segmento que você'],
    participante: {
      id: 'u-7',
      nome: 'Juliana Ferraz',
      fotoUrl: null,
      cargo: 'CTO',
      empresa: 'Datavision Analytics',
      tipoPerfil: 'EMPRESARIO',
      bio: 'Plataforma de BI com IA generativa. 200+ clientes em LatAm.',
      tagsBusca: [{ id: 't-jur-3', nome: 'Recrutamento Executivo' }],
      tagsOferta: [
        { id: 't-tech-4', nome: 'IA & Automação' },
        { id: 't-tech-5', nome: 'Engenharia de Software' },
      ],
    },
    createdAt: hoursAgo(3),
  },
  {
    id: 'm-7',
    status: 'SUGERIDO',
    score: 109,
    altoPotencial: false,
    motivos: ['Recrutador executivo para C-Level', 'Networking ALTO'],
    participante: {
      id: 'u-8',
      nome: 'Fernando Castro',
      fotoUrl: null,
      cargo: 'Sócio-Fundador',
      empresa: 'CastroExec',
      tipoPerfil: 'CONSULTOR',
      bio: 'Headhunter C-Level com 12 anos. Especialidade em tech e finanças.',
      tagsBusca: [{ id: 't-tech-1', nome: 'Tecnologia / SaaS' }],
      tagsOferta: [{ id: 't-jur-3', nome: 'Recrutamento Executivo' }],
    },
    createdAt: hoursAgo(20),
  },
  {
    id: 'm-8',
    status: 'CONVERSANDO',
    score: 131,
    altoPotencial: true,
    motivos: ['Match já aceito', 'Especialista em planejamento tributário'],
    participante: {
      id: 'u-9',
      nome: 'Ricardo Tanaka',
      fotoUrl: null,
      cargo: 'Sócio-Diretor',
      empresa: 'TKB Contabilidade Empresarial',
      tipoPerfil: 'CONSULTOR',
      bio: 'Reestruturação tributária para empresas de R$ 5M-100M.',
      tagsBusca: [{ id: 't-jur-4', nome: 'Expansão Nacional' }],
      tagsOferta: [
        { id: 't-fin-4', nome: 'Planejamento Tributário' },
        { id: 't-fin-3', nome: 'Reestruturação Financeira' },
      ],
    },
    createdAt: daysAgo(2),
  },
  {
    id: 'm-9',
    status: 'SUGERIDO',
    score: 96,
    altoPotencial: false,
    motivos: ['Mesma fase: Crescimento', 'Estado MS'],
    participante: {
      id: 'u-10',
      nome: 'Beatriz Nascimento',
      fotoUrl: null,
      cargo: 'CEO',
      empresa: 'AgroVerde Cooperativa',
      tipoPerfil: 'EMPRESARIO',
      bio: 'Coopertiva agroindustrial em MS, focada em soja e milho sustentável.',
      tagsBusca: [{ id: 't-mkt-2', nome: 'Branding' }],
      tagsOferta: [{ id: 't-jur-2', nome: 'Parceria Comercial' }],
    },
    createdAt: hoursAgo(36),
  },
  {
    id: 'm-10',
    status: 'SUGERIDO',
    score: 141,
    altoPotencial: true,
    motivos: ['Branding executivo de alto padrão', 'Resolve seu gargalo de marca'],
    participante: {
      id: 'u-11',
      nome: 'Camila Vieira',
      fotoUrl: null,
      cargo: 'Diretora Criativa',
      empresa: 'Studio Vieira',
      tipoPerfil: 'CONSULTOR',
      bio: 'Branding e identidade visual para CEOs e founders. 60+ projetos.',
      tagsBusca: [{ id: 't-vendas-1', nome: 'Vendas B2B' }],
      tagsOferta: [
        { id: 't-mkt-2', nome: 'Branding' },
        { id: 't-mkt-4', nome: 'SEO & Conteúdo' },
      ],
    },
    createdAt: hoursAgo(6),
  },
  {
    id: 'm-11',
    status: 'SUGERIDO',
    score: 88,
    altoPotencial: false,
    motivos: ['Mesma região', 'Networking MÉDIO'],
    participante: {
      id: 'u-12',
      nome: 'Lucas Mariano',
      fotoUrl: null,
      cargo: 'Diretor de Operações',
      empresa: 'Logística Centro-Oeste',
      tipoPerfil: 'EXECUTIVO',
      bio: 'Operações logísticas para varejo regional. 18 anos de experiência.',
      tagsBusca: [{ id: 't-tech-2', nome: 'Transformação Digital' }],
      tagsOferta: [{ id: 't-gest-5', nome: 'Operações & Processos' }],
    },
    createdAt: hoursAgo(48),
  },
  {
    id: 'm-12',
    status: 'PULADO',
    score: 72,
    altoPotencial: false,
    motivos: ['Match descartado'],
    participante: {
      id: 'u-13',
      nome: 'Roberta Pacheco',
      fotoUrl: null,
      cargo: 'Gerente Comercial',
      empresa: 'Pacheco Imóveis',
      tipoPerfil: 'PROFISSIONAL',
      bio: 'Imobiliária familiar em Cuiabá. Buscando expandir para Campo Grande.',
      tagsBusca: [{ id: 't-mkt-1', nome: 'Marketing Digital' }],
      tagsOferta: [{ id: 't-jur-2', nome: 'Parceria Comercial' }],
    },
    createdAt: daysAgo(3),
  },
];

// ─────────────────────────── CONVERSAS (8) ────────────────────────────────────
export const mockConversations: Conversation[] = [
  {
    matchId: 'm-3',
    participanteId: 'u-5',
    participanteNome: 'Carlos Mendes',
    participanteFotoUrl: null,
    ultimaMensagem: 'Top, vamos marcar terça às 14h?',
    ultimaMensagemEm: minutesAgo(35),
    naoLidas: 2,
  },
  {
    matchId: 'm-8',
    participanteId: 'u-9',
    participanteNome: 'Ricardo Tanaka',
    participanteFotoUrl: null,
    ultimaMensagem: 'Envio a proposta detalhada hoje à noite.',
    ultimaMensagemEm: hoursAgo(4),
    naoLidas: 0,
  },
  {
    matchId: 'm-2',
    participanteId: 'u-3',
    participanteNome: 'Rafael Almeida',
    participanteFotoUrl: null,
    ultimaMensagem: 'Vou te enviar dois cases parecidos.',
    ultimaMensagemEm: hoursAgo(12),
    naoLidas: 0,
  },
  {
    matchId: 'm-6',
    participanteId: 'u-7',
    participanteNome: 'Juliana Ferraz',
    participanteFotoUrl: null,
    ultimaMensagem: 'Adoraria conhecer melhor o produto de vocês.',
    ultimaMensagemEm: hoursAgo(28),
    naoLidas: 1,
  },
  {
    matchId: 'm-1',
    participanteId: 'u-2',
    participanteNome: 'Ana Beatriz Soares',
    participanteFotoUrl: null,
    ultimaMensagem: 'Você é introdução pelo José?',
    ultimaMensagemEm: daysAgo(1),
    naoLidas: 0,
  },
  {
    matchId: 'm-10',
    participanteId: 'u-11',
    participanteNome: 'Camila Vieira',
    participanteFotoUrl: null,
    ultimaMensagem: 'Te mando portfolio amanhã cedo!',
    ultimaMensagemEm: daysAgo(2),
    naoLidas: 0,
  },
  {
    matchId: 'm-4',
    participanteId: 'u-4',
    participanteNome: 'Marina Yamashita',
    participanteFotoUrl: null,
    ultimaMensagem: 'Vamos agendar uma call rápida?',
    ultimaMensagemEm: daysAgo(3),
    naoLidas: 0,
  },
  {
    matchId: 'm-5',
    participanteId: 'u-6',
    participanteNome: 'Pedro Henrique Lima',
    participanteFotoUrl: null,
    ultimaMensagem: 'Tenho cases pra te mostrar.',
    ultimaMensagemEm: daysAgo(4),
    naoLidas: 0,
  },
];

// ─────────────────────────── MENSAGENS por match (15 totais no principal) ─────
export const mockMessagesByMatch: Record<string, Message[]> = {
  'm-3': [
    {
      id: 'msg-3-1',
      matchId: 'm-3',
      authorId: 'u-demo',
      type: 'TEXT',
      content: 'Carlos, prazer! Vi seu perfil e adoraria entender melhor a tese da Latitude.',
      read: true,
      createdAt: hoursAgo(8),
    },
    {
      id: 'msg-3-2',
      matchId: 'm-3',
      authorId: 'u-5',
      type: 'TEXT',
      content:
        'Henrique, também! Vimos no seu perfil que estão em crescimento, exatamente onde investimos. Bora marcar 30min?',
      read: true,
      createdAt: hoursAgo(7),
    },
    {
      id: 'msg-3-3',
      matchId: 'm-3',
      authorId: 'u-demo',
      type: 'TEXT',
      content: 'Perfeito. Quais datas estão livres?',
      read: true,
      createdAt: hoursAgo(6),
    },
    {
      id: 'msg-3-4',
      matchId: 'm-3',
      authorId: 'u-5',
      type: 'TEXT',
      content: 'Terça ou quarta de manhã. Fechado?',
      read: true,
      createdAt: hoursAgo(5),
    },
    {
      id: 'msg-3-5',
      matchId: 'm-3',
      authorId: 'u-demo',
      type: 'CTA_REUNIAO',
      content: JSON.stringify({
        titulo: 'Reunião sobre Series A',
        data: '2026-05-23',
        hora: '14:00',
        local: 'Stand Latitude Capital',
      }),
      read: true,
      createdAt: hoursAgo(4),
    },
    {
      id: 'msg-3-6',
      matchId: 'm-3',
      authorId: 'u-5',
      type: 'TEXT',
      content: 'Aceito a reunião. Só confirma o link do calendário.',
      read: true,
      createdAt: hoursAgo(3),
    },
    {
      id: 'msg-3-7',
      matchId: 'm-3',
      authorId: 'u-demo',
      type: 'CTA_VALOR',
      content: JSON.stringify({
        valor: 5000000,
        descricao: 'Ticket de investimento proposto na rodada Series A',
      }),
      read: true,
      createdAt: hoursAgo(2),
    },
    {
      id: 'msg-3-8',
      matchId: 'm-3',
      authorId: 'u-5',
      type: 'TEXT',
      content: 'Vou levar para o comitê. Cap table atualizado?',
      read: true,
      createdAt: hoursAgo(1),
    },
    {
      id: 'msg-3-9',
      matchId: 'm-3',
      authorId: 'u-demo',
      type: 'TEXT',
      content: 'Te envio até amanhã.',
      read: true,
      createdAt: minutesAgo(55),
    },
    {
      id: 'msg-3-10',
      matchId: 'm-3',
      authorId: 'u-5',
      type: 'CTA_PROXIMO_PASSO',
      content: JSON.stringify({
        descricao: 'Compartilhar cap table e P&L 2024-2026',
        dueDate: '2026-05-21',
      }),
      read: false,
      createdAt: minutesAgo(40),
    },
    {
      id: 'msg-3-11',
      matchId: 'm-3',
      authorId: 'u-5',
      type: 'TEXT',
      content: 'Top, vamos marcar terça às 14h?',
      read: false,
      createdAt: minutesAgo(35),
    },
  ],
  'm-8': [
    {
      id: 'msg-8-1',
      matchId: 'm-8',
      authorId: 'u-demo',
      type: 'TEXT',
      content: 'Ricardo, vimos seu trabalho com a TechFlow. Podemos conversar?',
      read: true,
      createdAt: daysAgo(2),
    },
    {
      id: 'msg-8-2',
      matchId: 'm-8',
      authorId: 'u-9',
      type: 'TEXT',
      content: 'Claro Henrique! Pode me passar o faturamento do último trimestre?',
      read: true,
      createdAt: daysAgo(1),
    },
    {
      id: 'msg-8-3',
      matchId: 'm-8',
      authorId: 'u-demo',
      type: 'TEXT',
      content: 'Envio até amanhã. Preciso de uma análise do regime tributário atual.',
      read: true,
      createdAt: hoursAgo(8),
    },
    {
      id: 'msg-8-4',
      matchId: 'm-8',
      authorId: 'u-9',
      type: 'TEXT',
      content: 'Envio a proposta detalhada hoje à noite.',
      read: true,
      createdAt: hoursAgo(4),
    },
  ],
  'm-2': [
    {
      id: 'msg-2-1',
      matchId: 'm-2',
      authorId: 'u-demo',
      type: 'TEXT',
      content: 'Rafael, vi que você ajudou 80+ founders. Estou em uma virada de operação.',
      read: true,
      createdAt: hoursAgo(24),
    },
    {
      id: 'msg-2-2',
      matchId: 'm-2',
      authorId: 'u-3',
      type: 'TEXT',
      content: 'Vou te enviar dois cases parecidos.',
      read: true,
      createdAt: hoursAgo(12),
    },
  ],
  'm-6': [
    {
      id: 'msg-6-1',
      matchId: 'm-6',
      authorId: 'u-7',
      type: 'TEXT',
      content: 'Adoraria conhecer melhor o produto de vocês.',
      read: false,
      createdAt: hoursAgo(28),
    },
  ],
};

// ─────────────────────────── CRM (14 entries) ─────────────────────────────────
export const mockCrmEntries: CrmEntry[] = [
  {
    id: 'crm-1',
    matchId: 'm-3',
    participanteId: 'u-5',
    participanteNome: 'Carlos Mendes',
    participanteFotoUrl: null,
    stage: 'PROPOSTA',
    valorPotencial: 5_000_000,
    atividades: [
      { id: 'a-1-1', tipo: 'REUNIAO', descricao: 'Pitch inicial', createdAt: hoursAgo(7) },
      {
        id: 'a-1-2',
        tipo: 'NOTA',
        descricao: 'Ticket de R$5M, fundo focado em B2B SaaS',
        createdAt: hoursAgo(5),
      },
      {
        id: 'a-1-3',
        tipo: 'EMAIL',
        descricao: 'Enviei one-pager + cap table',
        createdAt: hoursAgo(1),
      },
    ],
    createdAt: hoursAgo(8),
    updatedAt: minutesAgo(35),
  },
  {
    id: 'crm-2',
    matchId: 'm-2',
    participanteId: 'u-3',
    participanteNome: 'Rafael Almeida',
    participanteFotoUrl: null,
    stage: 'EM_CONVERSA',
    valorPotencial: null,
    atividades: [
      {
        id: 'a-2-1',
        tipo: 'NOTA',
        descricao: 'Mentor sênior, cases similares ao nosso',
        createdAt: hoursAgo(24),
      },
    ],
    createdAt: hoursAgo(24),
    updatedAt: hoursAgo(12),
  },
  {
    id: 'crm-3',
    matchId: 'm-8',
    participanteId: 'u-9',
    participanteNome: 'Ricardo Tanaka',
    participanteFotoUrl: null,
    stage: 'PROPOSTA',
    valorPotencial: 80_000,
    atividades: [
      {
        id: 'a-3-1',
        tipo: 'REUNIAO',
        descricao: 'Reunião sobre planejamento tributário',
        createdAt: daysAgo(2),
      },
      { id: 'a-3-2', tipo: 'EMAIL', descricao: 'Proposta anual recebida', createdAt: hoursAgo(4) },
    ],
    createdAt: daysAgo(2),
    updatedAt: hoursAgo(4),
  },
  {
    id: 'crm-4',
    matchId: 'm-1',
    participanteId: 'u-2',
    participanteNome: 'Ana Beatriz Soares',
    participanteFotoUrl: null,
    stage: 'LEAD',
    valorPotencial: null,
    atividades: [
      {
        id: 'a-4-1',
        tipo: 'NOTA',
        descricao: 'CEO da NovaTech, preciso entender melhor a tese',
        createdAt: hoursAgo(12),
      },
    ],
    createdAt: hoursAgo(12),
    updatedAt: hoursAgo(12),
  },
  {
    id: 'crm-5',
    matchId: 'm-6',
    participanteId: 'u-7',
    participanteNome: 'Juliana Ferraz',
    participanteFotoUrl: null,
    stage: 'EM_CONVERSA',
    valorPotencial: 250_000,
    atividades: [
      {
        id: 'a-5-1',
        tipo: 'LIGACAO',
        descricao: 'Call rápida sobre integração de BI',
        createdAt: hoursAgo(30),
      },
    ],
    createdAt: hoursAgo(30),
    updatedAt: hoursAgo(28),
  },
  {
    id: 'crm-6',
    matchId: 'm-10',
    participanteId: 'u-11',
    participanteNome: 'Camila Vieira',
    participanteFotoUrl: null,
    stage: 'LEAD',
    valorPotencial: null,
    atividades: [],
    createdAt: hoursAgo(6),
    updatedAt: hoursAgo(6),
  },
  {
    id: 'crm-7',
    matchId: 'm-4',
    participanteId: 'u-4',
    participanteNome: 'Marina Yamashita',
    participanteFotoUrl: null,
    stage: 'EM_CONVERSA',
    valorPotencial: 120_000,
    atividades: [
      {
        id: 'a-7-1',
        tipo: 'NOTA',
        descricao: 'Possível parceria de tecnologia',
        createdAt: daysAgo(3),
      },
    ],
    createdAt: daysAgo(3),
    updatedAt: daysAgo(2),
  },
  {
    id: 'crm-8',
    matchId: 'm-5',
    participanteId: 'u-6',
    participanteNome: 'Pedro Henrique Lima',
    participanteFotoUrl: null,
    stage: 'LEAD',
    valorPotencial: null,
    atividades: [],
    createdAt: daysAgo(4),
    updatedAt: daysAgo(4),
  },
  {
    id: 'crm-9',
    matchId: 'm-7',
    participanteId: 'u-8',
    participanteNome: 'Fernando Castro',
    participanteFotoUrl: null,
    stage: 'LEAD',
    valorPotencial: null,
    atividades: [],
    createdAt: daysAgo(8),
    updatedAt: daysAgo(8),
  },
  {
    id: 'crm-10',
    matchId: 'm-9',
    participanteId: 'u-10',
    participanteNome: 'Beatriz Nascimento',
    participanteFotoUrl: null,
    stage: 'EM_CONVERSA',
    valorPotencial: null,
    atividades: [
      {
        id: 'a-10-1',
        tipo: 'NOTA',
        descricao: 'Cooperativa AgroVerde, interesse em marca',
        createdAt: daysAgo(2),
      },
    ],
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1),
  },
  {
    id: 'crm-11',
    matchId: 'm-11',
    participanteId: 'u-12',
    participanteNome: 'Lucas Mariano',
    participanteFotoUrl: null,
    stage: 'LEAD',
    valorPotencial: null,
    atividades: [],
    createdAt: daysAgo(10),
    updatedAt: daysAgo(10),
  },
  {
    id: 'crm-12',
    matchId: 'm-3',
    participanteId: 'u-99',
    participanteNome: 'Marcos Oliveira',
    participanteFotoUrl: null,
    stage: 'FECHADO',
    valorPotencial: 350_000,
    atividades: [
      {
        id: 'a-12-1',
        tipo: 'REUNIAO',
        descricao: 'Contrato assinado para consultoria anual',
        createdAt: daysAgo(15),
      },
    ],
    createdAt: daysAgo(20),
    updatedAt: daysAgo(15),
  },
  {
    id: 'crm-13',
    matchId: 'm-3',
    participanteId: 'u-98',
    participanteNome: 'Patrícia Souza',
    participanteFotoUrl: null,
    stage: 'PROPOSTA',
    valorPotencial: 95_000,
    atividades: [
      { id: 'a-13-1', tipo: 'EMAIL', descricao: 'Proposta v2 enviada', createdAt: daysAgo(1) },
    ],
    createdAt: daysAgo(5),
    updatedAt: daysAgo(1),
  },
  {
    id: 'crm-14',
    matchId: 'm-3',
    participanteId: 'u-97',
    participanteNome: 'Gabriel Hoffman',
    participanteFotoUrl: null,
    stage: 'FECHADO',
    valorPotencial: 180_000,
    atividades: [
      {
        id: 'a-14-1',
        tipo: 'NOTA',
        descricao: 'Parceria fechada para co-marketing trimestral',
        createdAt: daysAgo(7),
      },
    ],
    createdAt: daysAgo(12),
    updatedAt: daysAgo(7),
  },
];

// ─────────────────────────── NOTIFICAÇÕES (10) ────────────────────────────────
export type MockNotification = {
  id: string;
  tipo: 'NEW_MATCH' | 'NEW_MESSAGE' | 'MUTUAL_MATCH' | 'CRM_REMINDER' | 'EVENT_REMINDER';
  titulo: string;
  descricao: string;
  createdAt: string;
  read: boolean;
};

export const mockNotifications: MockNotification[] = [
  {
    id: 'n-1',
    tipo: 'NEW_MATCH',
    titulo: 'Novo match de alto potencial',
    descricao: 'Ana Beatriz Soares, CEO da NovaTech Pagamentos (score 147)',
    createdAt: minutesAgo(8),
    read: false,
  },
  {
    id: 'n-2',
    tipo: 'NEW_MESSAGE',
    titulo: 'Nova mensagem de Carlos Mendes',
    descricao: '"Top, vamos marcar terça às 14h?"',
    createdAt: minutesAgo(35),
    read: false,
  },
  {
    id: 'n-3',
    tipo: 'MUTUAL_MATCH',
    titulo: 'Match mútuo com Juliana Ferraz',
    descricao: 'Vocês aceitaram conversar. Inicie agora!',
    createdAt: hoursAgo(2),
    read: false,
  },
  {
    id: 'n-4',
    tipo: 'CRM_REMINDER',
    titulo: 'Atividade pendente: Carlos Mendes',
    descricao: 'Compartilhar cap table até 21/05',
    createdAt: hoursAgo(4),
    read: false,
  },
  {
    id: 'n-5',
    tipo: 'NEW_MATCH',
    titulo: 'Match no seu setor',
    descricao: 'Rafael Almeida, Mentor sênior (score 142)',
    createdAt: hoursAgo(8),
    read: true,
  },
  {
    id: 'n-6',
    tipo: 'EVENT_REMINDER',
    titulo: 'Painel "Crescer no Centro-Oeste" começa em 30 min',
    descricao: 'Auditório B, 15h30',
    createdAt: hoursAgo(12),
    read: true,
  },
  {
    id: 'n-7',
    tipo: 'CRM_REMINDER',
    titulo: 'Conversa fria há 7 dias',
    descricao: 'Pedro Henrique Lima, sem retorno desde 12/05',
    createdAt: daysAgo(1),
    read: true,
  },
  {
    id: 'n-8',
    tipo: 'NEW_MATCH',
    titulo: 'Match de mesma região',
    descricao: 'Beatriz Nascimento, AgroVerde Cooperativa',
    createdAt: daysAgo(1),
    read: true,
  },
  {
    id: 'n-9',
    tipo: 'EVENT_REMINDER',
    titulo: 'Know How Summit começa amanhã',
    descricao: 'Centro de Convenções Bosque dos Ipês, 22/05 09h',
    createdAt: daysAgo(2),
    read: true,
  },
  {
    id: 'n-10',
    tipo: 'MUTUAL_MATCH',
    titulo: 'Camila Vieira aceitou seu interesse',
    descricao: 'Inicie a conversa para falar sobre branding',
    createdAt: daysAgo(2),
    read: true,
  },
];
